import axios from 'axios';

// API base URL - adjust based on your backend server
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Store for auth token getter function
let getAuthToken = null;

// Function to set the auth token getter (called from ClerkTokenProvider)
export const setAuthTokenGetter = (tokenGetter) => {
  getAuthToken = tokenGetter;
};

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor for auth
apiClient.interceptors.request.use(
  async (config) => {
    // Add Clerk auth token if available
    if (getAuthToken) {
      try {
        const token = await getAuthToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      } catch (error) {
        // Auth token retrieval failed silently
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Contract Services
export const contractService = {
  // Deploy contract
  deployContract: async (data) => {
    const response = await apiClient.post('/contracts/deploy', data);
    return response.data;
  },

  // Get contracts by user
  getUserContracts: async (walletAddress) => {
    const response = await apiClient.get(`/contracts/user/${walletAddress}`);
    return response.data;
  },

  // Get all contracts
  getAllContracts: async (params = {}) => {
    const response = await apiClient.get('/contracts', { params });
    return response.data;
  },

  // Get contract details
  getContractDetails: async (contractAddress) => {
    const response = await apiClient.get(`/contracts/${contractAddress}`);
    return response.data;
  },

  // Verify contract
  verifyContract: async (contractAddress) => {
    const response = await apiClient.post(`/contracts/${contractAddress}/verify`);
    return response.data;
  },
};

// Transaction Services
export const transactionService = {
  // Transfer tokens
  transfer: async (data) => {
    const response = await apiClient.post('/transactions/transfer', data);
    return response.data;
  },

  // Mint tokens
  mint: async (data) => {
    const response = await apiClient.post('/transactions/mint', data);
    return response.data;
  },

  // Get balance
  getBalance: async (contractAddress, walletAddress, chain) => {
    const response = await apiClient.get(
      `/transactions/balance/${contractAddress}/${walletAddress}`,
      { params: { chain } }
    );
    return response.data;
  },

  // Get transaction history
  getTransactionHistory: async (walletAddress, params = {}) => {
    const response = await apiClient.get(
      `/transactions/history/${walletAddress}`,
      { params }
    );
    return response.data;
  },

  // Get transaction details
  getTransactionDetails: async (transactionId) => {
    const response = await apiClient.get(`/transactions/${transactionId}`);
    return response.data;
  },
};

// Health check
export const healthCheck = async () => {
  try {
    const response = await apiClient.get('/health');
    return response.data;
  } catch (error) {
    throw new Error('Backend server is not available');
  }
};

// Error handling helper
export const handleApiError = (error) => {
  if (error.response) {
    // Server responded with error status
    return error.response.data.message || 'Server error occurred';
  } else if (error.request) {
    // Request was made but no response received
    return 'No response from server. Please check your connection.';
  } else {
    // Something else happened
    return error.message || 'An unexpected error occurred';
  }
};

// Authenticated fetch wrapper with JSON response validation
// Use this instead of raw fetch() for API calls that need auth
export const authFetch = async (url, options = {}, signal) => {
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  // Add Clerk auth token if available
  if (getAuthToken) {
    try {
      const token = await getAuthToken();
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
    } catch (error) {
      // Token retrieval failed, proceed without it (cookies may still work)
    }
  }

  const response = await fetch(url, {
    ...options,
    headers,
    ...(signal ? { signal } : {}),
  });

  // Check if response is JSON before parsing
  const contentType = response.headers.get('content-type') || '';
  if (!response.ok) {
    if (contentType.includes('application/json')) {
      const errorData = await response.json();
      throw new Error(errorData.message || `Request failed: ${response.statusText}`);
    }
    throw new Error(`Request failed: ${response.status} ${response.statusText}`);
  }

  if (!contentType.includes('application/json')) {
    throw new Error('Server returned an unexpected response. Please try again.');
  }

  return response.json();
};

export default {
  contractService,
  transactionService,
  healthCheck,
  handleApiError,
};